<template>
  <div class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Channel' : 'Create New Channel' }}</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Information</h3>
        <div class="form-group half-width">
          <label for="name" class="required">Name</label>
          <input
            type="text"
            id="name"
            v-model="formData.name"
            class="form-control"
            maxlength="80"
            required
          >
        </div>

        <div v-if="isSuperuser()" class="form-group half-width">
          <label for="organization" class="required">Organization</label>
          <select
            id="organization"
            v-model="formData.organization_id"
            class="form-control"
            required
          >
            <option value="" disabled>Select an organization</option>
            <option v-for="org in sortedOrganizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <div class="form-group full-width">
          <label for="banner_url">Banner Image</label>
          <ImageUploadField
            v-model="formData.banner_url"
            folder="distributor_content"
            :placeholder="'Upload banner image'"
          />
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="formData.description"
            class="form-control"
            rows="4"
            placeholder="Max 2000 characters"
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="submit-button big">
          {{ isEditing ? 'Save Changes' : 'Create Channel' }}
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { handleApiError } from '@/scripts/shared-utils';
import ImageUploadField from '@/components/ImageUploadField.vue';
import { isSuperuser } from '@/scripts/permissions';

const route = useRoute();
const router = useRouter();
const isEditing = ref(false);
const channelId = ref(null);

const formData = ref({
  name: '',
  description: '',
  banner_url: '',
  organization_id: ''
});

const organizations = ref([]);

const sortedOrganizations = computed(() => {
  return [...organizations.value].sort((a, b) => a.name.localeCompare(b.name));
});

const handleSubmit = async () => {
  try {
    if (isEditing.value) {
      const editableFields = [
        'name',
        'description',
        'banner_url',
        'organization_id'
      ];
      
      const payload = {};
      editableFields.forEach(field => {
        if (field in formData.value) {
          payload[field] = formData.value[field];
        }
      });
      
      await axios.put(`/api/channels/${channelId.value}`, payload);
    } else {
      await axios.post(`/api/organizations/${formData.value.organization_id}/channels`, formData.value);
    }
    closeForm();
  } catch (error) {
    handleApiError(error, 'submitting channel');
    alert('Error saving channel. Please try again.');
  }
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/channels' : '/');
};

const fetchOrganizations = async () => {
  try {
    const response = await axios.get('/api/organizations');
    organizations.value = response.data;
  } catch (error) {
    handleApiError(error, 'fetching organizations');
  }
};

onMounted(async () => {
  if (isSuperuser()) {
    await fetchOrganizations();
  }
  isEditing.value = route.params.channel_id ? true : false;
  if (isEditing.value) {
    channelId.value = route.params.channel_id;
    try {
      const response = await axios.get(`/api/channels/${channelId.value}`);
      formData.value = {
        name: response.data.name,
        description: response.data.description,
        banner_url: response.data.banner_url || '',
        website_url: response.data.website_url || '',
        company: response.data.company || '',
        registration: response.data.registration || '',
        phone: response.data.phone || '',
        address_line_1: response.data.address_line_1 || '',
        address_line_2: response.data.address_line_2 || '',
        city: response.data.city || '',
        state: response.data.state || '',
        country: response.data.country || '',
        postal_code: response.data.postal_code || '',
      };
      if (isSuperuser()) {
        formData.value.organization_id = response.data.organization_id;
      }
    } catch (error) {
      handleApiError(error, 'fetching channel');
    }
  } 
});
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
